import React, { useState, useContext } from 'react';
import { Container, Card, Row, Col, Form, Button, Table, Alert, Spinner, Badge, Nav } from 'react-bootstrap';
import { Search, Database, User, Calendar, Receipt, ShoppingCart, Activity, ExternalLink } from 'lucide-react';
import AuthContext from "../../context/AuthContext";
import Layout from "../../UI/Layout";
import useHttpRequest from "../../hooks/useHttpRequest";

const AiSearchDashboard = () => {
    const { clinicId } = useContext(AuthContext);
    const [question, setQuestion] = useState("");
    const [results, setResults] = useState(null);
    const [viewMode, setViewMode] = useState('smart'); // 'smart' for structured, 'table' for raw SQL

    const { isLoading, error, sendRequest: fetchAiData } = useHttpRequest();

    // Helper to get icons based on the backend's iconType
    const getEntityIcon = (iconType) => {
        switch (iconType) {
            case 'person': return <User size={20} className="text-primary" />;
            case 'medical_services': return <Activity size={20} className="text-info" />;
            case 'calendar_today': return <Calendar size={20} className="text-success" />;
            case 'receipt': return <Receipt size={20} className="text-warning" />;
            case 'shopping_cart': return <ShoppingCart size={20} className="text-danger" />;
            default: return <Search size={20} className="text-muted" />;
        }
    };

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        if (!question.trim()) return;

        // Choose endpoint based on how we want to visualize it
        // /search returns List<NlqSearchResult>
        // /ask returns List<Map<String, Object>>
        const endpoint = viewMode === 'smart' ? 'search' : 'ask';

        fetchAiData(
            {
                url: `/nlq/${endpoint}?question=${encodeURIComponent(question)}&clinicId=${clinicId}`,
                method: "GET"
            },
            (data) => setResults(data)
        );
    };

    return (
        <Layout>
            <div className="bg-light py-4 min-vh-100">
                <Container>
                    <div className="mb-4 text-center">
                        <h2 className="fw-bold d-flex justify-content-center align-items-center gap-2">
                            <Database className="text-primary" size={32} />
                            Talk 2 Manish Tamaria
                        </h2>
                        <p className="text-muted">Query your clinic records using natural language</p>
                    </div>

                    <Card className="shadow-sm border-0 mb-4">
                        <Card.Body className="p-4">
                            <Form onSubmit={handleSearch}>
                                <Row className="g-2">
                                    <Col md={10}>
                                        <Form.Control
                                            size="lg"
                                            type="text"
                                            placeholder="Try: 'Show me patients from today' or 'Find unpaid invoices'..."
                                            value={question}
                                            onChange={(e) => setQuestion(e.target.value)}
                                            disabled={isLoading}
                                            className="border-2"
                                        />
                                    </Col>
                                    <Col md={2}>
                                        <Button
                                            variant="primary"
                                            size="lg"
                                            type="submit"
                                            className="w-100 d-flex align-items-center justify-content-center gap-2"
                                            disabled={isLoading || !question.trim()}
                                        >
                                            {isLoading ? <Spinner animation="border" size="sm" /> : <Search size={20} />}
                                            Search
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>

                            <div className="d-flex justify-content-center mt-3">
                                <Nav variant="pills" activeKey={viewMode} onSelect={(k) => setViewMode(k)}>
                                    <Nav.Item>
                                        <Nav.Link eventKey="smart" className="px-4">Smart View</Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="table" className="px-4">Data Table</Nav.Link>
                                    </Nav.Item>
                                </Nav>
                            </div>
                        </Card.Body>
                    </Card>

                    {error && (
                        <Alert variant="danger" className="shadow-sm">
                            <div className="fw-bold">Search encountered an issue:</div>
                            {error}
                        </Alert>
                    )}

                    {isLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="grow" variant="primary" />
                            <p className="mt-3 text-muted fw-medium">Processing your request...</p>
                        </div>
                    ) : results && results.length > 0 ? (
                        viewMode === 'smart' ? (
                            <Row className="g-3">
                                {results.map((item, idx) => (
                                    <Col md={6} key={idx}>
                                        <Card className="h-100 shadow-sm border-0 border-start border-4 border-primary hover-shadow transition">
                                            <Card.Body>
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div className="d-flex align-items-center gap-2">
                                                        {getEntityIcon(item.iconType)}
                                                        <Badge bg="light" text="dark" className="border">
                                                            {item.category}
                                                        </Badge>
                                                    </div>
                                                    <small className="text-muted">Match: {(item.relevanceScore * 100).toFixed(0)}%</small>
                                                </div>
                                                <h5 className="mb-1 fw-bold">{item.displayName}</h5>
                                                <p className="text-muted small mb-3">{item.summary}</p>

                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span className="badge bg-soft-primary text-primary px-2 py-1">
                                                        {item.entityType}
                                                    </span>
                                                    <Button
                                                        variant="link"
                                                        href={item.navigationUrl}
                                                        className="text-decoration-none p-0 d-flex align-items-center gap-1"
                                                    >
                                                        View Records <ExternalLink size={14} />
                                                    </Button>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        ) : (
                            <Card className="shadow-sm border-0">
                                <div className="table-responsive">
                                    <Table hover className="mb-0">
                                        <thead className="bg-light">
                                        <tr>
                                            {Object.keys(results[0]).map((key) => (
                                                <th key={key} className="text-uppercase small text-muted p-3">
                                                    {key.replace(/_/g, ' ')}
                                                </th>
                                            ))}
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {results.map((row, i) => (
                                            <tr key={i}>
                                                {Object.values(row).map((val, j) => (
                                                    <td key={j} className="p-3 align-middle">
                                                        {val === null ? <span className="text-muted">-</span> : val.toString()}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card>
                        )
                    ) : (
                        results && (
                            <div className="text-center py-5 bg-white rounded shadow-sm">
                                <h4 className="text-muted">No results found</h4>
                                <p>Try rephrasing your question or check your filters.</p>
                            </div>
                        )
                    )}
                </Container>
            </div>

            <style>{`
                .hover-shadow:hover { transform: translateY(-3px); box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important; }
                .transition { transition: all 0.2s ease-in-out; }
                .bg-soft-primary { background-color: rgba(13, 110, 253, 0.1); }
            `}</style>
        </Layout>
    );
};

export default AiSearchDashboard;